import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, TextField, Button, Paper, Alert, CircularProgress,
  Fade, Divider, Chip, Stepper, Step, StepLabel, InputAdornment,
  ToggleButtonGroup, ToggleButton
} from '@mui/material';
import { Search, Sparkles, Rocket, Building2, Check, Globe, ShoppingBag } from 'lucide-react';
import { styled } from '@mui/material/styles';
import axios from 'axios';

const SectionCard = styled(Paper)(({ theme }) => ({
  background: '#141926',
  border: '1px solid rgba(255,255,255,0.06)',
  borderRadius: 16,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const formatDomain = (input) => {
  return input
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
    .substring(0, 30);
};

const formatCNPJ = (v) => {
  const n = v.replace(/\D/g, '').substring(0, 14);
  if (n.length <= 2) return n;
  if (n.length <= 5) return `${n.slice(0, 2)}.${n.slice(2)}`;
  if (n.length <= 8) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5)}`;
  if (n.length <= 12) return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8)}`;
  return `${n.slice(0, 2)}.${n.slice(2, 5)}.${n.slice(5, 8)}/${n.slice(8, 12)}-${n.slice(12)}`;
};

export default function FtmIA() {
  const navigate = useNavigate();
  const [cnpjInput, setCnpjInput] = useState('');
  const [cnpjLoading, setCnpjLoading] = useState(false);
  const [cnpjData, setCnpjData] = useState(null);

  const [formData, setFormData] = useState({
    dominio: '', telefone: '', email: '',
    instagram: '', whatsapp: '', linkPagina: '',
    pixelMeta: '', appId: '',
  });

  const [iaLoading, setIaLoading] = useState(false);
  const [textos, setTextos] = useState({ missao: '', sobre: '', rodape: '' });
  const [textosGerados, setTextosGerados] = useState(false);

  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('padrao');

  const getUserTemplates = () => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      return user.allowedTemplates || ['padrao'];
    } catch { return ['padrao']; }
  };
  const allowedTemplates = getUserTemplates();

  const activeStep = !cnpjData ? 0 : !textosGerados ? 1 : 2;

  const handleBuscarCNPJ = async () => {
    const cnpjLimpo = cnpjInput.replace(/\D/g, '');
    if (cnpjLimpo.length !== 14) {
      setError('CNPJ deve ter 14 dígitos');
      return;
    }
    setError('');
    setCnpjLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/sites/cnpj/${cnpjLimpo}`,
        { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 }
      );
      if (res.data.success) {
        const d = res.data.data;
        setCnpjData(d);
        const razao = d.razao_social || '';
        setFormData(prev => ({
          ...prev,
          dominio: formatDomain(razao),
        }));
      } else {
        setError(res.data.message || 'CNPJ não encontrado');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao buscar CNPJ. Verifique e tente novamente.');
    } finally {
      setCnpjLoading(false);
    }
  };

  const handleGerarTextos = async () => {
    if (!formData.telefone || !formData.email) {
      setError('Telefone e Email são obrigatórios');
      return;
    }
    const currentTokens = getUserTokens();
    if (currentTokens < IA_TOKEN_COST) {
      setError(`Tokens insuficientes. A geração com IA custa ${IA_TOKEN_COST} token (R$ 40,00). Você tem ${currentTokens} token(s).`);
      return;
    }
    setError('');
    setIaLoading(true);
    try {
      const d = cnpjData;
      const endereco = [d.logradouro, d.numero, d.bairro, `${d.municipio}-${d.uf}`, `CEP ${d.cep}`].filter(Boolean).join(', ');
      const cnaePrincipal = d.cnae_fiscal_descricao ? { codigo: String(d.cnae_fiscal), descricao: d.cnae_fiscal_descricao } : null;
      const cnaesSecundarios = (d.cnaes_secundarios || []).map(c => ({ codigo: String(c.codigo), descricao: c.descricao }));

      const situacaoMap = { 1: 'Nula', 2: 'Ativa', 3: 'Suspensa', 4: 'Inapta', 8: 'Baixada' };

      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sites/gerar-textos-ia`,
        {
          razao_social: d.razao_social,
          nome_fantasia: d.nome_fantasia || d.razao_social,
          cnpj: formatCNPJ(String(d.cnpj)),
          data_abertura: d.data_inicio_atividade ? new Date(d.data_inicio_atividade).toLocaleDateString('pt-BR') : 'N/A',
          porte: d.porte || d.descricao_porte || 'N/A',
          natureza_juridica: d.natureza_juridica || d.descricao_natureza_juridica || 'N/A',
          capital_social: (d.capital_social || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 }),
          situacao_cadastral: situacaoMap[d.situacao_cadastral] || d.descricao_situacao_cadastral || 'Ativa',
          endereco,
          cnae_principal: cnaePrincipal,
          cnaes_secundarios: cnaesSecundarios,
          email: formData.email,
          telefone: formData.telefone,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      if (res.data.success) {
        setTextos({ missao: res.data.missao, sobre: res.data.sobre, rodape: res.data.rodape });
        setTextosGerados(true);
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        user.tokens = res.data.tokensRestantes ?? (user.tokens - IA_TOKEN_COST);
        localStorage.setItem('user', JSON.stringify(user));
      } else {
        setError(res.data.message || 'Erro ao gerar textos');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao gerar textos com IA. Tente novamente.');
    } finally {
      setIaLoading(false);
    }
  };

  const IA_TOKEN_COST = 1;

  const getUserTokens = () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    return user.tokens || 0;
  };

  const handleCriarSite = async () => {
    setError('');
    setSuccess('');
    setCreating(true);
    try {
      const config = {
        DOMINIO: formatDomain(formData.dominio),
        'RAZÃO': cnpjData.razao_social,
        CNPJ: formatCNPJ(String(cnpjData.cnpj)),
        'MISSÃO': textos.missao.replace(/\n/g, '<br>'),
        SOBRE: textos.sobre.replace(/\n/g, '<br>'),
        RODAPE: textos.rodape.replace(/\n/g, '<br>'),
        TELEFONE: formData.telefone,
        EMAIL: formData.email,
        INSTAGRAM: formData.instagram || '',
        WHATSAPP: formData.whatsapp || formData.telefone,
        LINK_PAGINA: formData.linkPagina || '',
        PIXEL_META: formData.pixelMeta || '',
        APP_ID: formData.appId || '',
        META_TAG: '',
      };

      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/sites/generate`,
        { config, iaGenerated: true, template: selectedTemplate },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 60000 }
      );
      if (res.data.success) {
        setSuccess('Site criado com sucesso!');
        setTimeout(() => navigate('/meus-sites'), 2000);
      } else {
        setError(res.data.message || 'Erro ao criar site');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao criar site. Tente novamente.');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Fade in timeout={400}>
      <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 1, sm: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Sparkles size={32} strokeWidth={1.75} color="#7C3AED" />
          <Typography variant="h4" sx={{ color: '#fff' }}>FTM IA</Typography>
          <Chip label="BETA" size="small" sx={{ background: 'rgba(124,58,237,0.2)', color: '#A78BFA', fontWeight: 700, fontSize: '0.7rem' }} />
        </Box>
        <Typography sx={{ color: '#9CA3AF', mb: 2 }}>
          Gere sites automaticamente com inteligência artificial. Basta informar o CNPJ.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4, p: 2, borderRadius: 3, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', flexWrap: 'wrap' }}>
          <Chip label="2 tokens" sx={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B', fontWeight: 700 }} />
          <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
            Sites gerados com IA custam <strong style={{ color: '#F59E0B' }}>R$ 8,00</strong> (2 tokens).
            Seus tokens: <strong style={{ color: getUserTokens() >= IA_TOKEN_COST ? '#10B981' : '#EF4444' }}>{getUserTokens()}</strong>
          </Typography>
        </Box>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {['Buscar CNPJ', 'Gerar com IA', 'Criar Site'].map((label) => (
            <Step key={label}>
              <StepLabel sx={{ '& .MuiStepLabel-label': { color: '#9CA3AF' }, '& .Mui-active .MuiStepLabel-label': { color: '#A78BFA' }, '& .Mui-completed .MuiStepLabel-label': { color: '#10B981' } }}>
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {/* ETAPA 1: Buscar CNPJ */}
        <SectionCard>
          <Typography variant="h6" sx={{ color: '#fff', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Search size={20} strokeWidth={1.75} color="#7C3AED" /> Buscar CNPJ
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <TextField
              fullWidth label="CNPJ" placeholder="00.000.000/0000-00"
              value={cnpjInput}
              onChange={(e) => setCnpjInput(formatCNPJ(e.target.value))}
              onKeyDown={(e) => e.key === 'Enter' && handleBuscarCNPJ()}
            />
            <Button
              variant="contained" onClick={handleBuscarCNPJ}
              disabled={cnpjLoading}
              sx={{ minWidth: 140, height: 56, borderRadius: 3 }}
              startIcon={cnpjLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <Search size={20} strokeWidth={1.75} />}
            >
              {cnpjLoading ? 'Buscando...' : 'Buscar'}
            </Button>
          </Box>

          {cnpjData && (
            <Fade in timeout={300}>
              <Box sx={{ mt: 3, p: 2, borderRadius: 3, background: 'rgba(16,185,129,0.05)', border: '1px solid rgba(16,185,129,0.2)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Check size={20} strokeWidth={1.75} color="#10B981" />
                  <Typography sx={{ color: '#10B981', fontWeight: 600 }}>Empresa encontrada</Typography>
                </Box>
                <Typography sx={{ color: '#fff', fontWeight: 600, fontSize: '1.1rem' }}>{cnpjData.razao_social}</Typography>
                {cnpjData.nome_fantasia && (
                  <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem' }}>Nome Fantasia: {cnpjData.nome_fantasia}</Typography>
                )}
                <Typography sx={{ color: '#9CA3AF', fontSize: '0.9rem' }}>
                  {cnpjData.municipio}/{cnpjData.uf} - {cnpjData.cnae_fiscal_descricao}
                </Typography>
              </Box>
            </Fade>
          )}
        </SectionCard>

        {/* ETAPA 2: Dados manuais + Gerar IA */}
        {cnpjData && (
          <Fade in timeout={400}>
            <SectionCard>
              <Typography variant="h6" sx={{ color: '#fff', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Building2 size={20} strokeWidth={1.75} color="#7C3AED" /> Informações do Site
              </Typography>

              <TextField
                fullWidth label="Domínio" value={formData.dominio}
                onChange={(e) => setFormData(prev => ({ ...prev, dominio: formatDomain(e.target.value) }))}
                sx={{ mb: 2 }} placeholder="ex: horizontes-servicos"
                helperText={`${formData.dominio}.ftmpainel.com.br`}
                InputProps={{ endAdornment: <InputAdornment position="end"><Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>{formData.dominio.length}/30</Typography></InputAdornment> }}
              />

              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
                <TextField fullWidth label="Telefone" value={formData.telefone} required
                  onChange={(e) => setFormData(prev => ({ ...prev, telefone: e.target.value }))}
                  placeholder="(31) 97139-1218"
                />
                <TextField fullWidth label="Email" value={formData.email} required
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="contato@empresa.com.br"
                />
              </Box>

              <Divider sx={{ borderColor: 'rgba(124,58,237,0.1)', my: 2 }} />
              <Typography sx={{ color: '#6B7280', fontSize: '0.85rem', mb: 2 }}>Opcionais</Typography>

              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
                <TextField fullWidth label="Instagram" value={formData.instagram}
                  onChange={(e) => setFormData(prev => ({ ...prev, instagram: e.target.value }))}
                  placeholder="https://instagram.com/empresa"
                />
                <TextField fullWidth label="WhatsApp" value={formData.whatsapp}
                  onChange={(e) => setFormData(prev => ({ ...prev, whatsapp: e.target.value }))}
                  placeholder="5531971391218"
                />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' }, mb: 2 }}>
                <TextField fullWidth label="Link da Página" value={formData.linkPagina}
                  onChange={(e) => setFormData(prev => ({ ...prev, linkPagina: e.target.value }))}
                  placeholder="https://facebook.com/empresa"
                />
                <TextField fullWidth label="App ID" value={formData.appId}
                  onChange={(e) => setFormData(prev => ({ ...prev, appId: e.target.value }))}
                />
              </Box>
              <TextField fullWidth label="Pixel Meta" value={formData.pixelMeta} multiline rows={2}
                onChange={(e) => setFormData(prev => ({ ...prev, pixelMeta: e.target.value }))}
                sx={{ mb: 3 }}
              />

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                {getUserTokens() < IA_TOKEN_COST && (
                  <Alert severity="warning" sx={{ width: '100%', mb: 1 }}>
                    Você precisa de pelo menos {IA_TOKEN_COST} tokens para gerar com IA. Você tem {getUserTokens()} token(s).
                  </Alert>
                )}
                <Button
                  variant="contained" size="large" onClick={handleGerarTextos}
                  disabled={iaLoading || getUserTokens() < IA_TOKEN_COST}
                  startIcon={iaLoading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <Sparkles size={20} strokeWidth={1.75} />}
                  sx={{ px: 4, py: 1.5, borderRadius: 3, fontSize: '1rem', '&.Mui-disabled': { background: 'rgba(107,114,128,0.3)', color: 'rgba(255,255,255,0.4)' } }}
                >
                  {iaLoading ? 'Gerando com IA...' : 'Gerar Textos com FTM IA (1 token)'}
                </Button>
                <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>
                  Será debitado 1 token (R$ 40,00) ao gerar
                </Typography>
              </Box>
            </SectionCard>
          </Fade>
        )}

        {/* ETAPA 3: Preview editável */}
        {textosGerados && (
          <Fade in timeout={400}>
            <SectionCard>
              <Typography variant="h6" sx={{ color: '#fff', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Sparkles size={20} strokeWidth={1.75} color="#10B981" /> Textos Gerados
              </Typography>
              <Typography sx={{ color: '#9CA3AF', mb: 3, fontSize: '0.9rem' }}>
                Revise e edite os textos antes de criar o site.
              </Typography>

              <Typography sx={{ color: '#A78BFA', fontWeight: 600, mb: 1, fontSize: '0.85rem' }}>MISSÃO</Typography>
              <TextField
                fullWidth multiline rows={4} value={textos.missao}
                onChange={(e) => setTextos(prev => ({ ...prev, missao: e.target.value }))}
                sx={{ mb: 3 }}
              />

              <Typography sx={{ color: '#A78BFA', fontWeight: 600, mb: 1, fontSize: '0.85rem' }}>SOBRE A EMPRESA</Typography>
              <TextField
                fullWidth multiline rows={12} value={textos.sobre}
                onChange={(e) => setTextos(prev => ({ ...prev, sobre: e.target.value }))}
                sx={{ mb: 3 }}
              />

              <Typography sx={{ color: '#A78BFA', fontWeight: 600, mb: 1, fontSize: '0.85rem' }}>RODAPÉ & POLÍTICA DE PRIVACIDADE</Typography>
              <TextField
                fullWidth multiline rows={10} value={textos.rodape}
                onChange={(e) => setTextos(prev => ({ ...prev, rodape: e.target.value }))}
                sx={{ mb: 4 }}
              />

              {allowedTemplates.length > 1 && (
                <Box sx={{ mb: 3 }}>
                  <Typography sx={{ color: '#A78BFA', fontWeight: 600, mb: 1, fontSize: '0.85rem' }}>MODELO DO SITE</Typography>
                  <ToggleButtonGroup
                    value={selectedTemplate}
                    exclusive
                    onChange={(_, v) => { if (v) setSelectedTemplate(v); }}
                    sx={{ display: 'flex', gap: 1 }}
                  >
                    <ToggleButton value="padrao" sx={{
                      flex: 1, textTransform: 'none', color: '#9CA3AF', border: '1px solid rgba(124,58,237,0.3)',
                      '&.Mui-selected': { background: 'rgba(124,58,237,0.15)', color: '#7C3AED', borderColor: '#7C3AED' }
                    }}>
                      <Globe size={18} style={{ marginRight: 8 }} /> Padrão
                    </ToggleButton>
                    <ToggleButton value="organic" sx={{
                      flex: 1, textTransform: 'none', color: '#9CA3AF', border: '1px solid rgba(124,58,237,0.3)',
                      '&.Mui-selected': { background: 'rgba(124,58,237,0.15)', color: '#7C3AED', borderColor: '#7C3AED' }
                    }}>
                      <ShoppingBag size={18} style={{ marginRight: 8 }} /> Organic
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                <Button
                  variant="contained" size="large" onClick={handleCriarSite}
                  disabled={creating}
                  startIcon={creating ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : <Rocket size={20} strokeWidth={1.75} />}
                  sx={{
                    px: 5, py: 1.5, borderRadius: 3, fontSize: '1.1rem',
                    background: '#10B981',
                    '&:hover': { background: '#059669' },
                    '&.Mui-disabled': { background: 'rgba(107,114,128,0.3)', color: 'rgba(255,255,255,0.4)' }
                  }}
                >
                  {creating ? 'Criando Site...' : 'Criar Site'}
                </Button>
                <Typography sx={{ color: '#6B7280', fontSize: '0.8rem' }}>
                  Os tokens já foram debitados na etapa anterior
                </Typography>
              </Box>
            </SectionCard>
          </Fade>
        )}
      </Box>
    </Fade>
  );
}
